import { useEffect, useState } from 'react'
import { isApiError } from '../../api/client'
import { Spinner } from '../../design'
import { AiCredentialsSection } from './AiCredentialsSection'
import { deriveChannelSignals } from '../status/derive'
import { useServiceStatusClear, useServiceStatusReporter } from '../status/useServiceStatus'
import { useChannels } from './channelQueries'
import { ChannelsSection } from './ChannelsSection'
import { SETTINGS_COPY } from './copy'
import { LockIcon } from './icons'
import { useMembers } from './memberQueries'
import { MembersSection } from './MembersSection'
import { MetaAppSection } from './MetaAppSection'
import { OperatorTerms } from './OperatorTerms'
import { TenantSettingsForm } from './TenantSettingsForm'
import { pickEditable, useTenantSettings, useUpdateTenantSettings, type EditableSettings } from './queries'

const useChannelSignals = (channels: ReturnType<typeof useChannels>) => {
  const report = useServiceStatusReporter()
  const clear = useServiceStatusClear()
  useEffect(() => {
    if (channels.data === undefined) return
    const signals = deriveChannelSignals(channels.data, new Date())
    if (signals.length === 0) {
      clear('channel_disconnected')
      return
    }
    for (const signal of signals) report(signal)
  }, [channels.data, clear, report])
}

export const AdminSettings = () => {
  const settings = useTenantSettings()
  const channels = useChannels()
  const members = useMembers()
  const update = useUpdateTenantSettings()
  const [draft, setDraft] = useState<EditableSettings | null>(null)

  useChannelSignals(channels)

  useEffect(() => {
    if (settings.data !== undefined && draft === null) setDraft(pickEditable(settings.data))
  }, [draft, settings.data])

  if (settings.isPending || draft === null) return <Spinner label={SETTINGS_COPY.loading} />

  const error = [settings.error, update.error, channels.error, members.error].find(isApiError)?.message ?? null

  return (
    <div className="settings-admin">
      <p className="settings-scope">
        <LockIcon />
        {SETTINGS_COPY.adminOnly}
      </p>

      {error === null ? null : (
        <p className="settings-error text-tone tone-danger" role="alert">
          {error}
        </p>
      )}

      <TenantSettingsForm
        draft={draft}
        choices={settings.data?.providerChoices ?? []}
        onChange={setDraft}
        onSave={() => update.mutate(draft)}
        saving={update.isPending}
        saved={update.isSuccess}
      />

      <AiCredentialsSection />
      <OperatorTerms fields={settings.data?.operatorFields} />
      <MetaAppSection />
      <ChannelsSection />
      <MembersSection />
    </div>
  )
}
